import { is } from 'stdlib/is'
import { Object } from 'stdlib/Object'

import type { IStorage, TStorageType, IDummyStorage } from './definitions'

import getNamespacedKey from './utils/getNamespacedKey'
import getStorage       from './utils/getStorage'

/**
 * Namespacelt wrapper a natív localStorage fölött.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage}
 */
export default class Storage<TKey extends string = string> implements IStorage<TKey> {
  private readonly ns: string
  private readonly storage: typeof window.localStorage
  private readonly storageType: TStorageType

  /**
   * Storage példányosítása.
   * @param namespace   - A namespace, amikhez párosítjuk a kulcs-értékeket.
   * @param storageType - Milyen típusú storage-ot szeretnénk példányosítani.
   */
  public constructor (namespace = '', storageType: TStorageType = 'local') {
    this.ns = namespace
    this.storageType = storageType
    this.storage = getStorage(storageType)
  }

  /**
   * Egy kulcshoz tartozó érték elkérése.
   * @param key - A kulcs, aminek az értékét kérjük.
   */
  public get (key: TKey): string | null {
    const nsKey = getNamespacedKey(this.ns, key)

    return this.storage.getItem(nsKey)
  }

  /**
   * Egy kulcs-érték páros megadása.
   * @param key   - A kulcs, amihez az értéket párosítjuk.
   * @param value - Az érték, ami a kulcshoz tartozik.
   */
  public set (key: TKey, value: string): void {
    const nsKey = getNamespacedKey(this.ns, key)
    this.storage.setItem(nsKey, value)
  }

  /**
   * Adott kulcs alapján létezik érték?
   * @param key - A kulcs, amit vizsgálunk.
   */
  public has (key: TKey): boolean {
    const nsKey = getNamespacedKey(this.ns, key)
    const item = this.storage.getItem(nsKey)

    return !is.null(item)
  }

  /**
   * Egy érték törlése, kulcs alapján.
   * @param key - A kulcs, ami alapján törölni szeretnénk.
   */
  public remove (key: TKey): void {
    const nsKey = getNamespacedKey(this.ns, key)
    this.storage.removeItem(nsKey)
  }

  /**
   * Ha létezik egy érték.
   * @param key   - A kulcs, amit állapottól függően beállítunk vagy törlünk.
   * @param value - A kulcshoz tartozó érték.
   */
  public toggle (key: TKey, value: string): void {
    if (this.has(key)) {
      this.remove(key)
    }
    else {
      this.set(key, value)
    }
  }

  /** Az adott namespace-hez tartozó összes érték törlése. */
  public clear (): void {
    const keys = this.storageType === 'dummy'
      ? (this.storage as IDummyStorage).getItemKeys()
      : Object.getKeys(this.storage)

    keys.forEach((key) => {
      const strKey = key as unknown as string

      if (strKey.startsWith(this.ns)) {
        this.storage.removeItem(key)
      }
    })
  }
}
